/**
 * The app-wide audio session. ONE StereoAnalyser, owned by the module — not by a view — so
 * navigating between the player and the gallery never tears the audio down (the old per-view
 * analysers detached on unmount, which is why switching views used to stop tab sharing).
 *
 * Two ways in:
 *   loadFile     — a dropped/chosen file plays on a hidden element (native to the speakers).
 *   captureInput — getUserMedia on an audio INPUT device (Stereo Mix / VB-Cable / a DJ
 *                  interface): NO sharing bar at all, music-grade constraints.
 * (plus the desktop's native capture, a LAN cast and a stem set — see below).
 *
 * Every source becomes ONE Web Audio graph (audio/graph.ts analyse()), with TWO listeners on the same
 * node: the StereoAnalyser (the spectrum every world reads) and the HEARING tap (continuous PCM for
 * the attribution student → frame.drivers, what the fx worlds react to).
 *
 * This module is the SOURCE SWITCH: each way in ends the previous source (one `session` counter — stale
 * async work checks it before applying) and builds the new one's graph. The pieces each source uses live
 * beside it:
 *   audio/graph.ts   — the source graph: the StereoAnalyser + the hearing on the same node
 *   audio/hearing.ts — the hearing tap (frame.drivers, what the fx worlds react to)
 *   audio/stems.ts   — stem analysis (a stem set, the muted parallel stem session)
 *   audio/desktop.ts — the desktop shell's capture permission + sources
 *   cast/transport.ts, cast/control.ts, cast/session.ts — a LAN cast in (the TV receiver)
 *   cast/sender.ts   — the desktop sender's native cast state + the "Play on…" picker
 */
import type { StemId } from '@syqel/engine'
import { Engine } from '@syqel/ota-client'
import { surface, track } from './analytics'
import { createProcessorNode } from './processorNode'
import { analyse, analyseMedia, analyseStream, closeCtx } from './audio/graph'
import { autoSeparate, clearStemSet, inferStemId, setStemSet, stopSeparation, type StemEntry } from './audio/stems'
import { isNativeAudio } from './audio/desktop'
import { connectPcm, type PcmOwner, type RemoteOptions } from './cast/transport'
import { castOnPos, castReset, endSenderSession } from './cast/session'

// SOURCE DETECTION moved into the engine (SDK 0.8): the analyzers run with `detectSources: true`,
// which stamps frame.detected + frame.sources on every update(). The app only OVERRIDES
// frame.sources with real separated stems (sourcesFromStems) when a stem session is live —
// or, on a cast receiver, with the sender's CastSources snapshots (castProtocol 1.1, cast/session.ts).

let video: HTMLVideoElement | null = null
let label = ''
const listeners = new Set<(label: string) => void>()
let session = 0 // bumped on every source change — stale async work checks it before applying

/** A guard for async work started now: true while no newer source has replaced this one. */
function sessionGuard(): () => boolean {
  const mine = session
  return () => mine === session
}

/** The source changes: stem analysis and the muted stem session end, then any native/cast pipe. */
function endSources(): void {
  clearStemSet()
  stopSeparation()
  stopNative()
}

function setLabel(next: string): void {
  label = next
  listeners.forEach((fn) => fn(next))
}

/** Subscribe to source changes; fires immediately with the current label. */
export function onSource(fn: (label: string) => void): () => void {
  listeners.add(fn)
  fn(label)
  return () => listeners.delete(fn)
}

export function sourceLabel(): string {
  return label
}

export function loadFile(file: File): void {
  session += 1
  endSources()
  // a FRESH element per file: an element can feed only one AudioContext ever (the Safari path routes it
  // through one), and the previous source's context is closed by the switch
  if (video) {
    video.pause()
    URL.revokeObjectURL(video.src)
    video.removeAttribute('src')
  }
  video = document.createElement('video')
  video.playsInline = true
  video.src = URL.createObjectURL(file)
  video.play().catch(() => undefined)
  // native playback to the speakers; analysis taps a captured copy of the stream
  analyseMedia(video, sessionGuard())
  currentSrcId = '' // a file isn't an enumerable source — clears the menu dropdown selection
  setLabel(file.name)
  track('connect_audio', { method: 'file', surface: surface() })
  // NOTE: in-browser auto-separation is DISABLED — the vendored Demucs WASM (onnxruntime 1.14,
  // minimal op-set) rejects our model at load, and browser separation is batch (minutes/track)
  // regardless, so it's not the real-time goal. Real separation lives in the DESKTOP app (GPU +
  // driver lookahead). The web stays a sandbox: mix-voice visuals here, true instruments via the
  // multi-file stem drop (loadStems) for power users / pre-separated demo tracks.
  // The separator.ts + muted-session machinery is retained for a future onnxruntime-web revisit,
  // reachable via a dev flag so it stays wired (no dead code) without shipping the broken default.
  if (safeLocal('syqel.sep.auto') === '1') void autoSeparate(file, video, sessionGuard())
}

function safeLocal(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Load a set of pre-separated STEM files (e.g. Demucs output: vocals/drums/bass/…).
 * All stems decode into one AudioContext and start sample-locked, mixed to the speakers;
 * each stem is analyzed in isolation so the engine sees true instruments, not freq slices.
 */
export async function loadStems(files: File[]): Promise<boolean> {
  try {
    session += 1
    const current = sessionGuard()
    stopNative()
    stopSeparation()
    const ctx = new AudioContext()
    const master = ctx.createGain()
    master.connect(ctx.destination)
    const taken = new Set<StemId>()
    const sources: AudioBufferSourceNode[] = []
    const entries: StemEntry[] = []
    for (const file of files) {
      const bytes = await file.arrayBuffer()
      if (!current()) { closeCtx(ctx); return false } // a newer source replaced this one while it loaded
      const buf = await ctx.decodeAudioData(bytes)
      if (!current()) { closeCtx(ctx); return false }
      const src = ctx.createBufferSource()
      src.buffer = buf
      src.loop = true
      const gain = ctx.createGain()
      src.connect(gain)
      gain.connect(master)
      const analyser = new Engine.StereoAnalyser()
      analyser.attachSource(ctx, gain)
      sources.push(src)
      entries.push({ id: inferStemId(file.name, taken), analyser })
    }
    // the main mix analyser taps the master AFTER per-stem wiring (its attachSource
    // detaches whatever source came before — including closing a previous stem context)
    analyse(ctx, master)
    void ctx.resume()
    const t0 = ctx.currentTime + 0.15
    sources.forEach((s) => s.start(t0)) // sample-locked start
    setStemSet(entries)
    currentSrcId = ''
    setLabel(`stems ×${entries.length}`)
    track('connect_audio', { method: 'stems', surface: surface() })
    return true
  } catch (err) {
    console.warn('[syqel] stem load failed', err)
    clearStemSet()
    return false
  }
}

/** Audio input devices (loopback devices like Stereo Mix / VB-Cable appear here). */
export async function listInputs(): Promise<MediaDeviceInfo[]> {
  try {
    // a momentary permission grant unlocks device labels
    const probe = await navigator.mediaDevices.getUserMedia({ audio: true })
    probe.getTracks().forEach((t) => t.stop())
  } catch {
    /* denied — labels may be blank, list anyway */
  }
  const devices = await navigator.mediaDevices.enumerateDevices()
  return devices.filter((d) => d.kind === 'audioinput')
}

// THE CURRENT SOURCE id — so the menu dropdown can reflect what's actually connected. It's the
// native source id ('system' / 'cap:…' / 'pid:…') or a web input deviceId; '' for a file / stems
// (no enumerable source). The label (sourceLabel) is the human name; this is the select's value.
let currentSrcId = ''
export function currentSourceId(): string {
  return currentSrcId
}

let nativeWs: WebSocket | null = null
let nativeCtx: AudioContext | null = null
let nativeWorker: Worker | null = null // LAN cast: the socket lives in a Worker (cast/transport.ts)
let nativeRelease: (() => void) | null = null // the LAN pipe's own teardown (its upstream subscription)

/** The session's side of a cast connect (cast/transport.ts): superseded by any newer source; on open it
 *  takes the pipe over — the worker + context into the native slots, the node to both listeners. */
function pcmOwner(): PcmOwner {
  return {
    current: sessionGuard(),
    adopt: ({ worker, ctx, node, label: name, release }) => {
      nativeWorker = worker
      nativeRelease = release
      nativeCtx = ctx
      analyse(ctx, node)
      currentSrcId = ''
      setLabel(name)
    }
  }
}

/**
 * Page hidden/visible (a TV app sent to the background by HOME/BACK, then brought back). A hidden
 * receiver must go SILENT — webOS keeps a backgrounded web app alive, so without this the music kept
 * playing over the TV's launcher — and resume when shown. Suspends only the cast context; the socket
 * stays up (the relay's drop-oldest bounds the backlog, the worklet resyncs on return).
 */
export function setBackgrounded(hidden: boolean): void {
  if (!nativeCtx) return
  if (hidden) void nativeCtx.suspend().catch(() => undefined)
  else void nativeCtx.resume().catch(() => undefined)
}

/**
 * END the remote cast session (a TV receiver that left the screen). Unlike setBackgrounded this
 * releases everything — socket, worker, context — and bumps the session so a connect still dialling
 * (worklet load / socket open) can't finish and resurrect it. The receiver redials when it is shown.
 */
export function releaseRemote(): void {
  session += 1
  stopNative()
  // the socket is gone without a 'close' from its worker — the sender's session ends here all the same
  // (its entitlement, favorites, levels and scene must not outlive it: a TV shown again is itself)
  endSenderSession()
}

/** Stop native capture: close the socket + context and tell the Rust side to release the device. */
export function stopNative(): void {
  if (nativeWs) {
    try {
      nativeWs.close()
    } catch {
      /* already closing */
    }
    nativeWs = null
  }
  nativeRelease?.()
  nativeRelease = null
  if (nativeWorker) {
    try {
      nativeWorker.postMessage({ type: 'close' })
      nativeWorker.terminate()
    } catch {
      /* already gone */
    }
    nativeWorker = null
  }
  if (nativeCtx) {
    closeCtx(nativeCtx)
    nativeCtx = null
  }
  if (isNativeAudio()) void import('@tauri-apps/api/core').then(({ invoke }) => invoke('audio_stop')).catch(() => undefined)
}

/** Start native system-audio (or per-app `pid:<n>`) loopback capture — desktop only. */
export async function captureNative(sourceId = 'system', name = 'System Audio'): Promise<boolean> {
  if (!isNativeAudio()) return false
  try {
    session += 1
    const current = sessionGuard()
    endSources()
    const { invoke } = await import('@tauri-apps/api/core')
    const started = await invoke<{ port: number; sampleRate: number; channels: number }>('audio_start', { sourceId })
    // SUPERSEDED while awaiting (another source was picked): that source owns the session, so this capture
    // registers nothing — no context, no socket, no label (the next stopNative() releases the native side)
    if (!current()) return false
    const ctx = new AudioContext({ sampleRate: started.sampleRate })
    // local capture: tight 40ms cushion (same machine, no network jitter — just smoothing)
    const node = await createProcessorNode(ctx, { script: '/native-pcm-worklet.js', name: 'native-pcm', numberOfInputs: 0, outputChannels: 2, processorOptions: { targetMs: 40 } })
    if (!current()) { closeCtx(ctx); return false }
    // the playout clock on THIS socket's stream (castPlayoutSample): when the desktop casts, the scene it
    // hands the TVs is anchored to it (setCastScene) and the shell re-bases it onto each TV's stream
    castReset()
    node.port.onmessage = (e) => { if (e.data && e.data.type === 'pos') castOnPos(e.data, ctx) }
    // analysers don't pull the graph — a silent gain to the destination keeps the worklet running
    // without re-outputting the captured audio (which would echo).
    const silent = ctx.createGain()
    silent.gain.value = 0
    node.connect(silent)
    silent.connect(ctx.destination)
    await ctx.resume()
    if (!current()) { closeCtx(ctx); return false }
    const ws = new WebSocket(`ws://127.0.0.1:${started.port}`)
    ws.binaryType = 'arraybuffer'
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') return
      const pcm = new Float32Array(ev.data as ArrayBuffer)
      node.port.postMessage({ pcm, channels: started.channels }, [pcm.buffer])
    }
    nativeWs = ws
    nativeCtx = ctx
    analyse(ctx, node)
    currentSrcId = sourceId
    setLabel(name)
    track('connect_audio', { method: 'native', surface: surface() })
    return true
  } catch (err) {
    console.warn('[syqel] native capture failed', err)
    return false
  }
}

export async function captureRemote(wsUrl: string, opts: RemoteOptions = {}): Promise<boolean> {
  try {
    session += 1
    const owner = pcmOwner()
    endSources()
    const ok = await connectPcm(wsUrl, opts, owner)
    if (ok) track('connect_audio', { method: 'remote', surface: surface() })
    return ok
  } catch (err) {
    console.warn('[syqel] remote capture failed', err)
    return false
  }
}

export interface CastInfo {
  port: number
  sampleRate: number
  channels: number
  ip: string
  token: string
  url: string
}

/**
 * Desktop only: start a LAN CAST of `sourceId`. The native capture BROADCASTS to all clients, so the
 * desktop keeps visualizing (we connect to our own cast over localhost, silent — the audio's already
 * audible from this machine) AND the TV plays + visualizes it. `secure` adds a pairing token (off by
 * default for a quick home-LAN test → no token in the TV URL). Returns null off-desktop or on failure.
 */
export async function startCast(sourceId = 'system', secure = false): Promise<CastInfo | null> {
  if (!isNativeAudio()) return null
  try {
    session += 1
    const owner = pcmOwner() // taken now: a source picked while the cast starts supersedes it
    endSources()
    const { invoke } = await import('@tauri-apps/api/core')
    const info = await invoke<CastInfo>('audio_cast_start', { sourceId, secure })
    if (!owner.current()) return null
    // visualize locally too: attach to our OWN cast over localhost as a second consumer (silent)
    const q = info.token ? `?token=${encodeURIComponent(info.token)}` : ''
    await connectPcm(`ws://127.0.0.1:${info.port}${q}`, { audible: false, name: 'casting to TV' }, owner)
    if (!owner.current()) return null
    track('connect_audio', { method: 'cast', surface: surface() })
    return info
  } catch (err) {
    console.warn('[syqel] cast start failed', err)
    return null
  }
}

export async function captureInput(deviceId: string, name: string): Promise<boolean> {
  try {
    session += 1
    const current = sessionGuard()
    endSources()
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: { exact: deviceId },
        // music-grade: voice processing would wreck the mix
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 2
      }
    })
    if (!current()) { stream.getTracks().forEach((t) => t.stop()); return false } // superseded while asking
    analyseStream(stream)
    currentSrcId = deviceId
    setLabel(name || 'input device')
    track('connect_audio', { method: 'input', surface: surface() })
    return true
  } catch {
    return false
  }
}
